import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FolderPlus, NotePencil, Trash, Check, X, Plus, CaretDown, CaretRight, BookOpen, Ruler,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tally-style Account Master: Create Group + Create Ledger,
// with group-wise ledger listing and edit/delete on both.
export default function AccountMaster() {
  const [groups, setGroups] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    if (!openSection) return;
    const onEsc = (e) => { if (e.key === "Escape" && openSection) { e.preventDefault(); setOpenSection(null); } };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [openSection]);

  const load = async () => {
    try {
      const [g, l, u] = await Promise.all([
        axios.get(`${API}/account-groups`),
        axios.get(`${API}/account-ledgers`),
        axios.get(`${API}/units`),
      ]);
      setGroups(g.data.groups || []);
      setLedgers(l.data.ledgers || []);
      setUnits(u.data.units || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-16 text-center text-sm text-slate-400" data-testid="am-loading">Loading…</div>;

  const sections = [
    { key: "group", title: "Create Group", desc: `${groups.length} groups`, icon: <FolderPlus size={22} weight="duotone" />, accent: "emerald" },
    { key: "ledger", title: "Create Ledgers", desc: `${ledgers.length} ledgers`, icon: <BookOpen size={22} weight="duotone" />, accent: "blue" },
    { key: "unit", title: "Create Units", desc: `${units.length} units (GST UQC pre-loaded)`, icon: <Ruler size={22} weight="duotone" />, accent: "violet" },
  ];
  const accents = {
    emerald: "border-emerald-300 bg-emerald-50/60 hover:bg-emerald-100/70 text-emerald-900",
    blue: "border-blue-300 bg-blue-50/60 hover:bg-blue-100/70 text-blue-900",
    violet: "border-violet-300 bg-violet-50/60 hover:bg-violet-100/70 text-violet-900",
  };
  const titles = { group: "Create Group", ledger: "Create Ledgers", unit: "Create Units" };

  return (
    <div className="max-w-2xl space-y-3" data-testid="account-master">
      {sections.map((s) => (
        <button
          key={s.key}
          onClick={() => setOpenSection(s.key)}
          data-testid={`am-open-${s.key}`}
          className={`w-full flex items-center gap-4 px-6 py-5 border-2 rounded-sm text-left transition-colors ${accents[s.accent]}`}
        >
          {s.icon}
          <span className="flex-1">
            <span className="block text-sm font-black uppercase tracking-wider">{s.title}</span>
            <span className="block text-xs text-slate-500 mt-0.5">{s.desc}</span>
          </span>
          <CaretRight size={18} weight="bold" />
        </button>
      ))}

      {openSection && createPortal(
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4"
          data-testid="am-modal"
          onClick={() => setOpenSection(null)}
        >
          <div
            className="bg-white rounded-md border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 px-5 py-3 bg-slate-900 text-white flex items-center justify-between">
              <h2 className="text-sm font-black uppercase tracking-widest" data-testid="am-modal-title">{titles[openSection]}</h2>
              <button onClick={() => setOpenSection(null)} data-testid="am-modal-close"
                className="p-1.5 text-slate-300 hover:text-white hover:bg-slate-700 rounded-sm">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-4">
              {openSection === "group" && <GroupSection groups={groups} onChanged={load} />}
              {openSection === "ledger" && <LedgerSection groups={groups} ledgers={ledgers} onChanged={load} />}
              {openSection === "unit" && <UnitSection units={units} onChanged={load} />}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function GroupSection({ groups, onChanged }) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editParent, setEditParent] = useState("");
  const [listOpen, setListOpen] = useState(true);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/account-groups`, { name: name.trim(), parent_id: parentId || null });
      toast.success(`Group '${name.trim()}' created`);
      setName("");
      setParentId("");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const saveEdit = async (gid) => {
    try {
      await axios.put(`${API}/account-groups/${gid}`, { name: editName.trim(), parent_id: editParent || null });
      toast.success("Group updated");
      setEditId(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete group '${g.name}'?`)) return;
    try {
      await axios.delete(`${API}/account-groups/${g.id}`);
      toast.success("Group deleted");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="am-group-section">
      <div className="px-5 py-4 border-b border-slate-200 bg-emerald-50/60">
        <h2 className="text-sm font-black uppercase tracking-wider text-emerald-900 flex items-center gap-2">
          <FolderPlus size={18} weight="duotone" /> Create Group
        </h2>
      </div>
      <div className="p-5 border-b border-slate-100 flex flex-wrap gap-2">
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Name of Group"
          data-testid="am-group-name-input"
          className="flex-1 min-w-[180px] px-3 py-2 border border-slate-300 rounded-sm text-sm"
        />
        <select
          value={parentId} onChange={(e) => setParentId(e.target.value)}
          data-testid="am-group-parent-select"
          className="w-52 px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
        >
          <option value="">Under: Primary</option>
          {groups.map((g) => <option key={g.id} value={g.id}>Under: {g.name}</option>)}
        </select>
        <button onClick={add} disabled={busy || !name.trim()} data-testid="am-group-add-btn"
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-sm font-bold disabled:opacity-50 flex items-center gap-1">
          <Plus size={14} weight="bold" /> Add
        </button>
      </div>
      <button
        onClick={() => setListOpen((o) => !o)}
        data-testid="am-group-list-toggle"
        className="w-full px-5 py-2 bg-emerald-900 hover:bg-emerald-800 text-white text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
      >
        {listOpen ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
        List of Groups ({groups.length}) — click to {listOpen ? "hide" : "view"}
      </button>
      {listOpen && (
      <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="am-group-list">
        {groups.map((g) => (
          <div key={g.id} className="px-5 py-2 flex items-center gap-2 hover:bg-emerald-50/40 group" data-testid={`am-group-row-${g.id}`}>
            {editId === g.id ? (
              <>
                <input value={editName} onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveEdit(g.id)}
                  data-testid="am-group-edit-input" autoFocus
                  className="flex-1 px-2 py-1 border border-emerald-400 rounded-sm text-sm" />
                <select value={editParent} onChange={(e) => setEditParent(e.target.value)}
                  data-testid="am-group-edit-parent"
                  className="w-44 px-2 py-1 border border-emerald-300 rounded-sm text-xs bg-white">
                  <option value="">Under: Primary</option>
                  {groups.filter((x) => x.id !== g.id).map((x) => (
                    <option key={x.id} value={x.id}>Under: {x.name}</option>
                  ))}
                </select>
                <button onClick={() => saveEdit(g.id)} data-testid="am-group-edit-save" className="p-1.5 text-emerald-700 hover:bg-emerald-100 rounded-sm"><Check size={16} weight="bold" /></button>
                <button onClick={() => setEditId(null)} data-testid="am-group-edit-cancel" className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm font-semibold text-slate-800">
                  {g.name}
                  {g.parent_name && (
                    <span className="ml-2 text-[10px] font-bold text-slate-400" data-testid={`am-group-parent-label-${g.id}`}>under {g.parent_name}</span>
                  )}
                </span>
                {g.ledger_count > 0 && (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm">{g.ledger_count} ledger</span>
                )}
                <button onClick={() => { setEditId(g.id); setEditName(g.name); setEditParent(g.parent_id || ""); }} data-testid={`am-group-edit-${g.id}`}
                  title="Edit group" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm opacity-60 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                  <NotePencil size={15} />
                </button>
                <button onClick={() => del(g)} data-testid={`am-group-delete-${g.id}`}
                  title="Delete group" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm opacity-60 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                  <Trash size={15} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

function LedgerSection({ groups, ledgers, onChanged }) {
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editGroup, setEditGroup] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const add = async () => {
    if (!name.trim() || !groupId) return;
    setBusy(true);
    try {
      await axios.post(`${API}/account-ledgers`, { name: name.trim(), group_id: groupId });
      toast.success(`Ledger '${name.trim()}' created`);
      setName("");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const saveEdit = async (lid) => {
    try {
      await axios.put(`${API}/account-ledgers/${lid}`, { name: editName.trim(), group_id: editGroup });
      toast.success("Ledger updated");
      setEditId(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (l) => {
    if (!window.confirm(`Delete ledger '${l.name}'?`)) return;
    try {
      await axios.delete(`${API}/account-ledgers/${l.id}`);
      toast.success("Ledger deleted");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // group-wise split
  const byGroup = {};
  for (const l of ledgers) (byGroup[l.group_name] = byGroup[l.group_name] || []).push(l);
  const groupNames = Object.keys(byGroup).sort();

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="am-ledger-section">
      <div className="px-5 py-4 border-b border-slate-200 bg-blue-50/60">
        <h2 className="text-sm font-black uppercase tracking-wider text-blue-900 flex items-center gap-2">
          <BookOpen size={18} weight="duotone" /> Create Ledger
        </h2>
      </div>
      <div className="p-5 border-b border-slate-100 space-y-2">
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Name of Ledger"
          data-testid="am-ledger-name-input"
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
        />
        <div className="flex gap-2">
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="am-ledger-group-select"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
            <option value="">— Select Group (Under) —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <button onClick={add} disabled={busy || !name.trim() || !groupId} data-testid="am-ledger-add-btn"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-sm font-bold disabled:opacity-50 flex items-center gap-1">
            <Plus size={14} weight="bold" /> Add
          </button>
        </div>
      </div>
      <div className="px-5 py-2 bg-blue-900 text-white text-xs font-black uppercase tracking-widest text-center">
        Ledgers — Group Wise ({ledgers.length})
      </div>
      <div className="max-h-[480px] overflow-y-auto">
        {groupNames.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-8" data-testid="am-no-ledgers">No ledgers yet — create your first ledger above.</p>
        )}
        {groupNames.map((gn) => (
          <div key={gn} data-testid={`am-ledger-group-${gn}`}>
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [gn]: !c[gn] }))}
              className="w-full px-5 py-2 bg-slate-100 border-y border-slate-200 flex items-center gap-2 text-left hover:bg-slate-200/60"
              data-testid={`am-ledger-group-toggle-${gn}`}>
              {collapsed[gn] ? <CaretRight size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
              <span className="text-xs font-black uppercase tracking-wider text-slate-700 flex-1">{gn}</span>
              <span className="text-[10px] font-bold text-slate-500">{byGroup[gn].length}</span>
            </button>
            {!collapsed[gn] && byGroup[gn].map((l) => (
              <div key={l.id} className="px-5 py-2 flex items-center gap-2 hover:bg-blue-50/40 group border-b border-slate-50" data-testid={`am-ledger-row-${l.id}`}>
                {editId === l.id ? (
                  <>
                    <input value={editName} onChange={(e) => setEditName(e.target.value)}
                      data-testid="am-ledger-edit-input" autoFocus
                      className="flex-1 px-2 py-1 border border-blue-400 rounded-sm text-sm" />
                    <select value={editGroup} onChange={(e) => setEditGroup(e.target.value)} data-testid="am-ledger-edit-group"
                      className="px-2 py-1 border border-blue-300 rounded-sm text-xs bg-white max-w-[140px]">
                      {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                    <button onClick={() => saveEdit(l.id)} data-testid="am-ledger-edit-save" className="p-1.5 text-blue-700 hover:bg-blue-100 rounded-sm"><Check size={16} weight="bold" /></button>
                    <button onClick={() => setEditId(null)} data-testid="am-ledger-edit-cancel" className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-slate-800 pl-5">{l.name}</span>
                    <button onClick={() => { setEditId(l.id); setEditName(l.name); setEditGroup(l.group_id); }} data-testid={`am-ledger-edit-${l.id}`}
                      title="Edit ledger" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm opacity-60 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                      <NotePencil size={15} />
                    </button>
                    <button onClick={() => del(l)} data-testid={`am-ledger-delete-${l.id}`}
                      title="Delete ledger" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm opacity-60 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                      <Trash size={15} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function UnitSection({ units, onChanged }) {
  const [name, setName] = useState("");
  const [abbr, setAbbr] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAbbr, setEditAbbr] = useState("");
  const [listOpen, setListOpen] = useState(true);

  const add = async () => {
    if (!name.trim() || !abbr.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/units`, { name: name.trim(), abbreviation: abbr.trim() });
      toast.success(`Unit '${abbr.trim().toUpperCase()}' created`);
      setName(""); setAbbr("");
      setListOpen(true);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const saveEdit = async (uid) => {
    try {
      await axios.put(`${API}/units/${uid}`, { name: editName.trim(), abbreviation: editAbbr.trim() });
      toast.success("Unit updated");
      setEditId(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete unit '${u.abbreviation}' (${u.name})?`)) return;
    try {
      await axios.delete(`${API}/units/${u.id}`);
      toast.success("Unit deleted");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="am-unit-section">
      <div className="px-5 py-4 border-b border-slate-200 bg-violet-50/60">
        <h2 className="text-sm font-black uppercase tracking-wider text-violet-900 flex items-center gap-2">
          <Ruler size={18} weight="duotone" /> Create Units
        </h2>
        <p className="text-[11px] text-slate-500 mt-0.5">Government (GST UQC) standard units are pre-loaded — you can also add your own.</p>
      </div>
      <div className="p-5 border-b border-slate-100 flex flex-wrap gap-2">
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Unit full name (e.g. Kilograms)"
          data-testid="am-unit-name-input"
          className="flex-1 min-w-[200px] px-3 py-2 border border-slate-300 rounded-sm text-sm"
        />
        <input
          value={abbr} onChange={(e) => setAbbr(e.target.value.toUpperCase())}
          placeholder="Abbreviation (e.g. KGS)"
          maxLength={6}
          data-testid="am-unit-abbr-input"
          className="w-44 px-3 py-2 border border-slate-300 rounded-sm text-sm uppercase"
        />
        <button onClick={add} disabled={busy || !name.trim() || !abbr.trim()} data-testid="am-unit-add-btn"
          className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-sm font-bold disabled:opacity-50 flex items-center gap-1">
          <Plus size={14} weight="bold" /> Add
        </button>
      </div>
      <button
        onClick={() => setListOpen((o) => !o)}
        data-testid="am-unit-list-toggle"
        className="w-full px-5 py-2 bg-violet-900 hover:bg-violet-800 text-white text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-colors"
      >
        {listOpen ? <CaretDown size={13} weight="bold" /> : <CaretRight size={13} weight="bold" />}
        List of Units ({units.length}) — click to {listOpen ? "hide" : "view"}
      </button>
      {listOpen && (
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="am-unit-list">
          {units.map((u) => (
            <div key={u.id} className="px-5 py-2 flex items-center gap-2 hover:bg-violet-50/40 group" data-testid={`am-unit-row-${u.id}`}>
              {editId === u.id ? (
                <>
                  <input value={editAbbr} onChange={(e) => setEditAbbr(e.target.value.toUpperCase())}
                    data-testid="am-unit-edit-abbr" maxLength={6}
                    className="w-24 px-2 py-1 border border-violet-400 rounded-sm text-sm uppercase" />
                  <input value={editName} onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit(u.id)}
                    data-testid="am-unit-edit-name" autoFocus
                    className="flex-1 px-2 py-1 border border-violet-400 rounded-sm text-sm" />
                  <button onClick={() => saveEdit(u.id)} data-testid="am-unit-edit-save" className="p-1.5 text-violet-700 hover:bg-violet-100 rounded-sm"><Check size={16} weight="bold" /></button>
                  <button onClick={() => setEditId(null)} data-testid="am-unit-edit-cancel" className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
                </>
              ) : (
                <>
                  <span className="w-16 text-sm font-black text-violet-800">{u.abbreviation}</span>
                  <span className="flex-1 text-sm text-slate-800">{u.name}</span>
                  {!u.is_default && (
                    <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-sm">custom</span>
                  )}
                  <button onClick={() => { setEditId(u.id); setEditName(u.name); setEditAbbr(u.abbreviation); }} data-testid={`am-unit-edit-${u.id}`}
                    title="Edit unit" className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm opacity-60 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                    <NotePencil size={15} />
                  </button>
                  <button onClick={() => del(u)} data-testid={`am-unit-delete-${u.id}`}
                    title="Delete unit" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm opacity-60 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                    <Trash size={15} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
